// =============================================================================
// sales-attempts.ts — Contrato Zod compartilhado das tentativas de contato
// comercial (`contact_attempts`, F34-S01/F34-S03).
//
// Fonte única consumida pela API (validação de request/response,
// modules/sales/attempts) e pelo frontend (F34-S05) — nenhum dos dois lados
// redeclara enums ou o shape de resposta.
//
// DECISÃO DE MODELAGEM herdada de `contactAttempts.ts` (doc 26 §4.3, não
// reabrir): estado TÉCNICO da chamada (`technicalState`) é separado do
// desfecho COMERCIAL (`outcomeCode`) — duas colunas distintas.
//
// Convenção de payload (igual a sales-appointments.ts): camelCase em toda a
// borda, paginação via limit/offset, datas como string ISO 8601 (UTC, com
// offset) ou null.
//
// LGPD (doc 17 §8.3/§8.5): `notes` e `nextStep` são texto livre preenchido
// por humano e podem conter PII do titular. O consumidor HTTP (API) garante
// redact em log — `req.body.notes`/`*.notes` e
// `req.body.next_step`/`*.next_step`/`req.body.nextStep`/`*.nextStep` já
// cobertos NAS DUAS listas (`apps/api/src/app.ts` e `apps/api/src/lib/logger.ts`,
// fechado em F33-S02/F33-S09) — nunca persistir em payload de evento (outbox)
// sem tratamento.
// =============================================================================
import { z } from 'zod';
import { AttemptOriginSchema, CallTechnicalStateSchema, ContactChannelSchema, SalesOutcomeCodeSchema, } from './sales.js';
// ---------------------------------------------------------------------------
// Helper de querystring — scalar→array (mesmo padrão de sales-appointments.ts).
// ---------------------------------------------------------------------------
function arrayQueryParam(schema) {
    return z.preprocess((v) => (v === undefined || v === null ? undefined : Array.isArray(v) ? v : [v]), z.array(schema).optional());
}
// ---------------------------------------------------------------------------
// Response — uma tentativa de contato
// ---------------------------------------------------------------------------
export const ContactAttemptSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    /** Herdada do lead no momento da tentativa — nunca escolhida pelo ator. */
    cityId: z.string().uuid(),
    leadId: z.string().uuid(),
    /** Vínculo opcional com a campanha comercial. FK real desde F35-S01. */
    campaignId: z.string().uuid().nullable(),
    /** Vínculo opcional com o membro de campanha. FK real desde F35-S01. */
    campaignMemberId: z.string().uuid().nullable(),
    /** Compromisso de agenda que originou esta tentativa (retorno agendado → ligação feita). */
    appointmentId: z.string().uuid().nullable(),
    /** `agents.id` do vendedor que executou a tentativa — NÃO é `users.id`. */
    agentId: z.string().uuid(),
    /** `users.id` de quem registrou/disparou a tentativa. null = origem automática. */
    userId: z.string().uuid().nullable(),
    channel: ContactChannelSchema,
    origin: AttemptOriginSchema,
    /** Estado TÉCNICO da chamada — deliberadamente separado de `outcomeCode` (doc 26 §4.3). */
    technicalState: CallTechnicalStateSchema.nullable(),
    /** Desfecho comercial. null enquanto a tentativa não foi concluída/registrada. */
    outcomeCode: SalesOutcomeCodeSchema.nullable(),
    /** Identificador da chamada no provedor de telefonia (F36). Dado de TERCEIRO. */
    providerCallId: z.string().nullable(),
    /** Chave idempotente — garante que um retry não duplica a tentativa nem disca duas vezes. */
    idempotencyKey: z.string(),
    /** ISO 8601 com offset — início da tentativa (discagem/mensagem/chegada). */
    startedAt: z.string(),
    answeredAt: z.string().nullable(),
    endedAt: z.string().nullable(),
    durationSeconds: z.number().int().nonnegative().nullable(),
    /** Observação livre. Texto livre — PII potencial (doc 17 §8.3). */
    notes: z.string().nullable(),
    /** Próximo passo combinado com o lead. Texto livre — PII potencial (doc 17 §8.3). */
    nextStep: z.string().nullable(),
    /** Vínculo com a linha do tempo única do lead (`interactions.id`). */
    interactionId: z.string().uuid().nullable(),
    metadata: z.record(z.string(), z.unknown()),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ---------------------------------------------------------------------------
// List query — GET /api/sales/contact-attempts
// ---------------------------------------------------------------------------
export const ContactAttemptListQuerySchema = z.object({
    agentId: arrayQueryParam(z.string().uuid()),
    leadId: arrayQueryParam(z.string().uuid()),
    channel: arrayQueryParam(ContactChannelSchema),
    origin: arrayQueryParam(AttemptOriginSchema),
    outcomeCode: arrayQueryParam(SalesOutcomeCodeSchema),
    startedFrom: z.string().datetime({ offset: true }).optional(),
    startedTo: z.string().datetime({ offset: true }).optional(),
    sortBy: z.enum(['startedAt', 'createdAt']).default('startedAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const ContactAttemptListResponseSchema = z.object({
    data: z.array(ContactAttemptSchema),
    total: z.number().int().describe('Total de tentativas que atendem ao filtro'),
    limit: z.number().int(),
    offset: z.number().int(),
});
// ---------------------------------------------------------------------------
// Create — POST /api/sales/contact-attempts
// ---------------------------------------------------------------------------
export const ContactAttemptCreateBodySchema = z.object({
    leadId: z.string().uuid(),
    channel: ContactChannelSchema,
    /** Omitir = `manual` (agente iniciou fora de qualquer fila/discador). */
    origin: AttemptOriginSchema.optional(),
    /**
     * Omitir = o próprio agente do ator autenticado. Informar um agente
     * diferente exige `sales_appointments:manage` (verificado no service —
     * mesma regra fina de `AppointmentCreateBodySchema.assignedAgentId`).
     */
    agentId: z.string().uuid().optional(),
    /** Compromisso de agenda que originou esta tentativa (retorno agendado → ligação feita). */
    appointmentId: z.string().uuid().optional(),
    /**
     * Chave idempotente (doc 26 §14 passo 3). Omitir gera uma chave nova no
     * servidor — repetir a MESMA chave explícita não duplica a tentativa nem
     * disca duas vezes.
     */
    idempotencyKey: z.string().min(1).max(200).optional(),
    /** Omitir usa o instante do registro (now()). Permite lançar uma tentativa passada. */
    startedAt: z.string().datetime({ offset: true }).optional(),
    notes: z.string().min(1).max(4000).optional(),
    nextStep: z.string().min(1).max(2000).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
// ---------------------------------------------------------------------------
// Outcome — POST /api/sales/contact-attempts/:id/outcome
//
// Aplica os metadados de desfecho configuráveis por organização (F34-S02,
// `sales_outcome_codes` — fonte efetiva; `SALES_OUTCOME_META` é apenas o
// default lazy quando a organização nunca configurou):
//   - requiresNote:        `notes` não pode ficar vazio/ausente.
//   - requiresAppointment: `nextAppointment` (retorno) é obrigatório — criado
//     na MESMA transação do desfecho (doc 26 §22, "falha parcial não pode
//     perder o desfecho").
//   - blocksContact:       preferência de bloqueio é gravada em
//     `contact_preferences` para o(s) canal(is) do escopo do outcome.
// ---------------------------------------------------------------------------
const ContactAttemptNextAppointmentSchema = z.object({
    scheduledAt: z.string().datetime({ offset: true }),
    /** Omitir usa o default conservador de 30 minutos (mesmo default de `contact_appointments`). */
    durationMinutes: z.number().int().positive().max(1440).optional(),
    /** Omitir herda o canal da tentativa concluída. */
    channel: ContactChannelSchema.optional(),
    reason: z.string().min(1).max(500).optional(),
});
export const ContactAttemptOutcomeBodySchema = z.object({
    outcomeCode: SalesOutcomeCodeSchema,
    /** Estado TÉCNICO da chamada, quando aplicável (`channel='phone_integrated'`, F36). */
    technicalState: CallTechnicalStateSchema.optional(),
    /**
     * Observação de conclusão. Texto livre — PII potencial (doc 17 §8.3).
     * Obrigatória quando o outcome configurado exige nota
     * (`sales_outcome_codes.requires_note`, default `SALES_OUTCOME_META`).
     */
    notes: z.string().min(1).max(4000).optional(),
    nextStep: z.string().min(1).max(2000).optional(),
    answeredAt: z.string().datetime({ offset: true }).optional(),
    endedAt: z.string().datetime({ offset: true }).optional(),
    durationSeconds: z.number().int().nonnegative().optional(),
    /** Identificador da chamada no provedor de telefonia (F36). Dado de TERCEIRO. */
    providerCallId: z.string().optional(),
    /** Retorno agendado no mesmo fluxo. Obrigatório quando o outcome exige (`requiresAppointment`). */
    nextAppointment: ContactAttemptNextAppointmentSchema.optional(),
});
// ---------------------------------------------------------------------------
// Response — POST /api/sales/contact-attempts/:id/outcome
//
// `nextAppointment` reusa o shape de `AppointmentSchema` (sales-appointments.ts)
// — mesma entidade `contact_appointments`, apenas criada por este fluxo em
// vez de POST /api/sales/appointments/:id/complete.
// ---------------------------------------------------------------------------
export const ContactAttemptOutcomeResponseSchema = z.object({
    attempt: ContactAttemptSchema,
    /** Compromisso de retorno criado na mesma transação quando o outcome exige. null caso contrário. */
    nextAppointmentId: z.string().uuid().nullable(),
    /**
     * Casamento server-side (F38-S03, doc planejamento-livechat-contexto-comercial.md
     * §3, elos 1-2): o outcome deste registro resolve, na MESMA transação, o
     * membro de campanha ATIVO do lead (posse segura). `'claimed_by_other'`
     * quando esse membro estava `claimed` por OUTRO usuário — o servidor NÃO
     * tocou nele (nunca sobrescreve a reserva de outro vendedor). `null` nos
     * demais casos: resolvido com sucesso, ou nenhum membro ativo encontrado
     * para o lead nesta organização.
     */
    campaignMemberSkipped: z.enum(['claimed_by_other']).nullable(),
});
//# sourceMappingURL=sales-attempts.js.map